import React, { useState } from 'react';
import { Text, Flex } from '@chakra-ui/react';
import { ButtonText, Badge, OutlineCard } from '@morressier/alembic';

import { IAuthorWithEmail, IRoleType } from '@publishing-engine/domain/models';
import AuthorDetailsModal from './AuthorDetailsModal';
import { ISubmissionAuthor } from 'src/contexts/FormContext';
import Divider from 'src/components/Divider';
import { useSubmissionFields } from '../../../hooks/useSubmissionFields';
import {
  FormFieldAuthors,
  FormFieldContactAuthor,
  FormFieldCorrespondingAuthor,
  FormFieldSubmittingAuthor
} from '@publishing-engine/domain/types/article';
import { PhaseBanner } from '../PhaseBanner';
import SubmittedAuthorDetailsModal from './AuthorProfileModal/SubmittedAuthorDetailsModal';
import { GenericSubmissionField } from '../MySubmission/FormFieldGeneric/GenericSubmissionField';
import {
  useAuthorData,
  useAuthorRoleCheck
} from 'src/pages/Organization/Workspace/SubmissionContexts/Users/userHook';
import ProxyUserMenu from 'src/components/ProxyUser/ProxyUserMenu';
import { RenderUserBadgeList } from 'src/pages/Organization/Workspace/SubmissionContexts/Users/UserBadgeList';
import ComposeEmailModal, { AdHocModalOpen } from './ComposeEmailModal';
import {
  groupAuthorsByRoles,
  IAuthorRolesMap
} from 'src/utils/groupAuthorByRoles';
import ProxyUser from 'src/components/ProxyUser/ProxyUser';
import { Tooltip } from '@chakra-ui/tooltip';
import UserName from 'src/components/User/UserName';

type AuthorsTabProps = {
  roleContext?: IRoleType;
  submissionId?: string;
};

const AuthorsTab: React.FC<AuthorsTabProps> = ({
  roleContext,
  submissionId
}) => {
  const { getFieldState } = useSubmissionFields();
  const authors = getFieldState<FormFieldAuthors>('Authors')?.value;
  const submittingAuthorEmail =
    getFieldState<FormFieldSubmittingAuthor>('SubmittingAuthor')?.value!;
  const contactAuthorEmail =
    getFieldState<FormFieldContactAuthor>('ContactAuthor')?.value;
  const correspondingAuthorEmail = getFieldState<FormFieldCorrespondingAuthor>(
    'CorrespondingAuthor'
  )?.value;

  const roles: IAuthorRolesMap = {
    Submitting: submittingAuthorEmail,
    ...(contactAuthorEmail && {
      Contact: contactAuthorEmail
    }),
    ...(correspondingAuthorEmail && {
      Corresponding: correspondingAuthorEmail
    })
  };

  const [selectedAuthor, setSelectedAuthor] = React.useState<
    IAuthorWithEmail | ISubmissionAuthor | null
  >(null);

  const [openSendEmailModal, setOpenSendEmailModal] = useState<AdHocModalOpen>({
    status: false,
    defaultEmail: []
  });

  const AuthorCard = ({
    author,
    roles
  }: {
    author: IAuthorWithEmail | ISubmissionAuthor;
    roles: string[];
  }) => {
    const { data: authorData } = useAuthorData(author);
    const isUserEditor = author.id && useAuthorRoleCheck(author.id);

    return (
      <>
        {/* Author roles */}
        <OutlineCard padding={4} marginY={2}>
          <Flex flexDirection="column" gap={4} bgColor="surfacePrimaryFill">
            {roles.length && (
              <>
                <Flex gap={4}>
                  {roles?.map(role => (
                    <Badge variant="attention" size="context">
                      {role} Author
                    </Badge>
                  ))}
                </Flex>
                {Divider}
              </>
            )}
            {/* Avatar */}
            <Flex gap={4} alignItems={isUserEditor ? 'flex-start' : ''}>
              <Flex flexDirection="column" flex={1}>
                <Flex gap={2}>
                  <UserName
                    firstName={author.firstName}
                    middleName={author.middleName}
                    lastName={author.lastName}
                    lastSentStatus={authorData?.user?.lastSentEmailStatus}
                    enableIcon={Object.hasOwn(
                      authorData?.user ?? {},
                      'isEmailDelivered'
                    )}
                    isEmailDelivered={authorData?.user?.isEmailDelivered}
                    isActionLabel={false}
                    timeStamp={authorData?.user?.lastSentEmailTimeStamp}
                  />
                  <Tooltip
                    hasArrow
                    shouldWrapChildren
                    placement="top"
                    label="Proxy user"
                    borderRadius={4}
                  >
                    <ProxyUser
                      isDecisionMaker
                      user={{
                        userId: author?.id || authorData?.userId || '',
                        firstName:
                          author?.firstName ||
                          authorData?.user?.firstName ||
                          '',
                        lastName:
                          author?.lastName || authorData?.user?.lastName || ''
                      }}
                      role={'author'}
                      authorData={authorData}
                    />
                  </Tooltip>
                </Flex>

                <Text textStyle="textBody">
                  {author.affiliation}
                  {author.country && `, ${author.country}`}
                </Text>
                <Text>{author.email}</Text>

                {authorData && (
                  <Flex wrap={'wrap'} gap={3} marginTop={2}>
                    <RenderUserBadgeList
                      data={authorData.tags ?? []}
                      hasIcon={true}
                    />
                  </Flex>
                )}
                <Flex gap={4} flexDirection="row" mt={2}>
                  {isUserEditor ? (
                    <ProxyUserMenu
                      isProxyUser={isUserEditor}
                      handleClick={() => setSelectedAuthor(author)}
                      user={{
                        firstName: author.firstName,
                        lastName: author.lastName,
                        userId: author.id || authorData?.userId || '',
                        email: author.email
                      }}
                      setOpenSendEmailModal={setOpenSendEmailModal}
                    />
                  ) : (
                    <ButtonText onClick={() => setSelectedAuthor(author)}>
                      View Details
                    </ButtonText>
                  )}
                  <ButtonText
                    onClick={() =>
                      setOpenSendEmailModal({
                        status: true,
                        defaultEmail: [author.email]
                      })
                    }
                  >
                    Send Email
                  </ButtonText>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </OutlineCard>
      </>
    );
  };

  const renderAuthors = () =>
    groupAuthorsByRoles(roles, authors).map(author => (
      <AuthorCard key={author.email} author={author} roles={author.roles} />
    ));

  return (
    <>
      {selectedAuthor ? (
        selectedAuthor?.id ? (
          <SubmittedAuthorDetailsModal
            userId={selectedAuthor?.id || ''}
            author={selectedAuthor}
            onBackClick={() => setSelectedAuthor(null)}
            showReviewerActivity={true}
            roleContext={roleContext}
            submissionId={submissionId}
          />
        ) : (
          // Author Details Modal
          <AuthorDetailsModal
            author={selectedAuthor}
            onBackClick={() => setSelectedAuthor(null)}
          />
        )
      ) : null}

      <Flex gap={3} flexDirection="column">
        <PhaseBanner role="editorial" />
        <Flex flexDirection="column" alignItems="flex-start" gap={2}>
          <Text textStyle="headingSection">Authors</Text>
          <Text textStyle="textBody">
            Thoroughly review the information for all authors in this
            submission.
          </Text>
        </Flex>

        {/* Author Role */}
        <Text textStyle="headingGroup">Author Roles</Text>

        {renderAuthors()}

        <GenericSubmissionField fieldName="Authors" roleContext={roleContext} />
      </Flex>
      {openSendEmailModal.status && (
        <ComposeEmailModal
          setOpenSendEmailModal={setOpenSendEmailModal}
          email={openSendEmailModal.defaultEmail}
        />
      )}
    </>
  );
};

export default AuthorsTab;
